import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ProjectContext } from '../../../context/ProjectContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { SelectBar } from '../selectBar'
import { createPortal } from 'react-dom'
import { useDebounce } from '../../../hooks/useDebounce'
import { EditDialog } from '../../dialogs/editDialog'

export const Feedback = ({ feedback, selected, handleFeedbackSelect, feedbackHover, setFeedbackHover }) => {
  const { setFeedbacks, meetings, project, setProject, participants } = useContext(ProjectContext)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [feedbackContent, setFeedbackContent] = useState('')
  const [showPercentage, setShowPercentage] = useState(false)
  const debouncedFeedbackContent = useDebounce(feedbackContent, 1000)
  const divRef = useRef(null)
  const textRef = useRef(null)
  const [smallFontSize, setSmallFontSize] = useState(false)
  const [truncateText, setTruncateText] = useState(0)
  const [truncateHover, setTruncateHover] = useState(false)
  const customFetch = useCustomFetch()

  useEffect(() => {
    if (textRef.current) {
      // Get the computed styles for the <p> element
      let styles = window.getComputedStyle(textRef.current)
      let lineHeight = parseFloat(styles.lineHeight)
      let height = textRef.current.offsetHeight
      let lines = Math.ceil(height / lineHeight)

      if (lines < 6) return
      setSmallFontSize(true)

      textRef.current.style.fontSize = '12px'
      textRef.current.style.lineHeight = '16px'

      styles = window.getComputedStyle(textRef.current)
      lineHeight = parseFloat(styles.lineHeight)
      height = textRef.current.offsetHeight
      lines = Math.ceil(height / lineHeight)

      if (lines > 6) {
        // here I need to truncate the text to get 6 lines no more
        // I want amount of chars from note.content to be equal to 6 lines
        styles = window.getComputedStyle(textRef.current)
        lineHeight = parseFloat(styles.lineHeight)
        let amountChars = feedback.content.length
        while (lines > 6 && amountChars > 0) {
          amountChars -= 3
          textRef.current.innerText = feedback.content.slice(0, amountChars)
          height = textRef.current.offsetHeight
          lines = Math.ceil(height / lineHeight)
        }
        textRef.current.innerText += feedback.content
        setTruncateText(amountChars - 3)
      }
    }
  }, [feedback.content])

  useEffect(() => {
    if (!feedbackContent) return
    const handleChangeFeedbackContent = async (content) => {
      try {
        setFeedbacks((prevFeedbacks) => prevFeedbacks.map((f) => (f.id === feedback.id ? { ...f, content } : f)))
        await customFetch('/updateFeedbackContent', 'PUT', { feedbackId: feedback.id, content })
      } catch (error) {
        console.log(error)
        toast.error('Whoops! Something went wrong. Please try again.')
      }
    }
    handleChangeFeedbackContent(feedbackContent)
  }, [debouncedFeedbackContent])

  const handleDeleteFeedback = async () => {
    try {
      setFeedbacks((prevFeedbacks) => prevFeedbacks.filter((f) => f.id !== feedback.id))
      setShowEditDialog(false)
      toast.success('Feedback deleted successfully')
      await customFetch('/deleteFeedbacks', 'DELETE', { feedbacks: [feedback.id], projectId: feedback.projectId })
    } catch (error) {
      console.log(error)
      toast.error('Whoops! Something went wrong. Please try again.')
    }
  }

  return (
    <div
      onDoubleClick={() => setShowEditDialog(true)}
      key={feedback.id}
      onMouseEnter={() => selected && setFeedbackHover(feedback.id)}
      onClick={(event) => handleFeedbackSelect(feedback.id, event)}
      className={'m-2 relative'}
    >
      {/* Cluster badge positioned absolutely in top-right corner */}
      {Array.isArray(feedback.clustered_notes) &&
        feedback.clustered_notes.length > 0 &&
        feedback.meetings_count > 1 && (
          <div
            className="absolute -top-2 -right-2"
            onMouseEnter={() => setShowPercentage(true)}
            onMouseLeave={() => setShowPercentage(false)}
          >
            <div
              className={`relative transition-all duration-200 ease-in-out ${showPercentage ? 'w-[48px]' : 'w-[32px]'} h-[32px]`}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                className={`absolute transition-opacity duration-200 ${showPercentage ? 'opacity-0' : 'opacity-100'}`}
              >
                <circle cx="16" cy="16" r="13" fill="black" stroke="#333333" strokeWidth="3" />
                <circle
                  cx="16"
                  cy="16"
                  r="13"
                  fill="none"
                  stroke="#FF4A8E"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${(Math.round((feedback.meetings_count * 100) / meetings.length) / 100) * 81.68} 81.68`}
                  transform="rotate(-90 16 16)"
                />
              </svg>
              <div
                className={`absolute inset-0 flex items-center justify-center ${showPercentage ? 'bg-black rounded-full px-2' : ''}`}
              >
                <span className="text-white text-sm font-bold font-['Manrope'] leading-none transition-all duration-200 whitespace-nowrap">
                  {showPercentage
                    ? `${Math.round((feedback.meetings_count * 100) / meetings.length)}%`
                    : feedback.meetings_count}
                </span>
              </div>
            </div>
          </div>
        )}

      {selected && feedbackHover === feedback.id && <SelectBar noteOrFeedback="feedback" />}
      {showEditDialog &&
        createPortal(
          <EditDialog
            entity={feedback}
            project={project}
            participants={participants}
            setProject={setProject}
            setEntities={setFeedbacks}
            entityName={'feedback'}
            meeting={meetings.find((meet) => meet.id === feedback.meetingId) || {}}
            close={() => setShowEditDialog(false)}
            changeContent={(content) => setFeedbackContent(content)}
            deleteEntity={handleDeleteFeedback}
          />,
          document.body
        )}
      <div
        ref={divRef}
        onMouseEnter={() => truncateText && setTruncateHover(true)}
        onMouseLeave={() => truncateText && setTruncateHover(false)}
        style={{
          backgroundColor: `${feedback.sentiment_analysis === 'positive' ? '#BBE6B7' : feedback.sentiment_analysis === 'negative' ? '#E5A8AC' : '#FFF98E'}`,
        }}
        className={`w-[210px] h-[134px] p-5 rounded shadow ${truncateText && 'transition-transform duration-300 hover:w-[230px] hover:h-[154px] hover:-m-[10px]'}  ${selected && 'border-2 border-pink rounded-[6px]'}`}
      >
        <p
          ref={textRef}
          className={`text-black font-semibold font-['Manrope'] ${smallFontSize ? 'text-xs' : 'text-sm'}`}
        >
          {truncateText && !truncateHover ? feedback.content.slice(0, truncateText) + '...' : feedback.content}
        </p>
      </div>
    </div>
  )
}

Feedback.propTypes = {
  feedback: PropTypes.object,
  selected: PropTypes.bool,
  handleFeedbackSelect: PropTypes.func,
  feedbackHover: PropTypes.string,
  setFeedbackHover: PropTypes.func,
}
